// The words the billing API speaks in, and what the app says back.
//
// Every decision in the billing flow switches on a machine code — the `code` a
// billing failure carries, the `reason` a billing status carries — and never on
// the human text next to it. The text is the server's to reword; the codes are
// the contract. See README.md in this folder.

/** The `code` a billing endpoint answers a failure with. */
export const BillingCode = Object.freeze({
   UNAUTHENTICATED: "UNAUTHENTICATED",
   NOT_ORGANIZATION_ADMIN: "NOT_ORGANIZATION_ADMIN",
   ORGANIZATION_NOT_FOUND: "ORGANIZATION_NOT_FOUND",
   PACKET_NOT_FOUND: "PACKET_NOT_FOUND",
   PACKET_HAS_NO_PRICE: "PACKET_HAS_NO_PRICE",
   NO_SUBSCRIPTION: "NO_SUBSCRIPTION",
   // change-plan, from an annual plan to a monthly one before the last month of
   // its 12-month commitment. A bigger annual plan is allowed now; a smaller
   // one is scheduled for the commitment's end.
   COMMITMENT_ACTIVE: "COMMITMENT_ACTIVE",
   NO_STRIPE_CUSTOMER: "NO_STRIPE_CUSTOMER",
   NOT_STRIPE: "NOT_STRIPE",
   EMAIL_NOT_CONFIRMED: "EMAIL_NOT_CONFIRMED",
   BILLING_PROFILE_INCOMPLETE: "BILLING_PROFILE_INCOMPLETE",
   BILLING_PROFILE_INVALID: "BILLING_PROFILE_INVALID",
   TAX_ID_INVALID: "TAX_ID_INVALID",
   CONSENT_REQUIRED: "CONSENT_REQUIRED",
   CONSENT_AMOUNT_MISMATCH: "CONSENT_AMOUNT_MISMATCH",
   SETUP_INTENT_NOT_SUCCEEDED: "SETUP_INTENT_NOT_SUCCEEDED",
   SETUP_INTENT_FOREIGN: "SETUP_INTENT_FOREIGN",
   PAYMENT_ACTION_REQUIRED: "PAYMENT_ACTION_REQUIRED",
   PAYMENT_FAILED: "PAYMENT_FAILED",
   // pay-outstanding with no card saved: 409. The card form is the way on.
   PAYMENT_METHOD_REQUIRED: "PAYMENT_METHOD_REQUIRED",
   PROMO_CODE_INVALID: "PROMO_CODE_INVALID",
   PROMO_CODE_NOT_APPLICABLE: "PROMO_CODE_NOT_APPLICABLE",
   BILLING_BUSY: "BILLING_BUSY",
   STRIPE_ERROR: "STRIPE_ERROR",

   // The organization's active workspaces, or the active projects in one of
   // them, don't fit: a smaller plan (change-plan, updateSubscriptionPlan), or
   // a workspace or project unarchived over the plan's limit (planChanges.js).
   PLAN_LIMIT_EXCEEDED: "PLAN_LIMIT_EXCEEDED",

   // A plan cheaper than the one the organization had, picked after its
   // subscription was cancelled — set to end, or already ended (change-plan,
   // subscriptions, updateSubscriptionPlan). Only support makes that change.
   DOWNGRADE_REQUIRES_SUPPORT: "DOWNGRADE_REQUIRES_SUPPORT",

   // A new plan (subscriptions, change-plan, the old createSubscription) while an
   // annual plan that ended unpaid still owes money: 409, with the status and the
   // debt (amountDue) in `data`. The debt is paid first, then a plan is picked.
   OUTSTANDING_DEBT: "OUTSTANDING_DEBT",

   // A change for the end of the period or commitment (change-plan,
   // updateSubscriptionPlan) that Stripe didn't leave scheduled: 502, nothing
   // changed, and the same request can be sent again.
   PLAN_CHANGE_NOT_SCHEDULED: "PLAN_CHANGE_NOT_SCHEDULED",

   // A change for the end of the period or commitment (change-plan,
   // updateSubscriptionPlan) while the subscription owes money — past_due,
   // unpaid, or an unpaid annual plan: 409, nothing changed. The payment is
   // settled first. An upgrade paid now never gets it.
   PAYMENT_DUE: "PAYMENT_DUE",

   // Not the server's. The names this app gives the failures that arrive with
   // no code at all: a connection that dropped, and the rate limiter, which
   // answers before any billing code runs.
   NETWORK_ERROR: "NETWORK_ERROR",
   RATE_LIMITED: "RATE_LIMITED"
});

/** Why an organization is held at `billing_only` — the `reason` on its billing status. */
export const BillingReason = Object.freeze({
   NO_SUBSCRIPTION: "NO_SUBSCRIPTION",
   TRIAL_ENDED_NO_PAYMENT_METHOD: "TRIAL_ENDED_NO_PAYMENT_METHOD",
   PAYMENT_FAILED: "PAYMENT_FAILED",
   PAYMENT_ACTION_REQUIRED: "PAYMENT_ACTION_REQUIRED",
   SUBSCRIPTION_CANCELED: "SUBSCRIPTION_CANCELED",
   /**
    * A plan was picked with no free trial left and no card on file. Nothing is
    * subscribed or charged yet: saving a card creates the subscription (the
    * status's packetId / isMonthly) and takes the first payment.
    */
   PAYMENT_METHOD_REQUIRED: "PAYMENT_METHOD_REQUIRED",
   /**
    * An annual plan reached the end of its contract unpaid and ended, and its
    * invoices up to then are still owed (`amountDue`, kind outstanding_debt).
    * Paying them settles the debt and nothing else; a plan is picked after.
    * Only billing-status and the payment answers say it: a sign in, which
    * reads no invoices, says SUBSCRIPTION_CANCELED for the same organization.
    */
   OUTSTANDING_DEBT: "OUTSTANDING_DEBT",
   /**
    * Enterprise was picked and no subscription runs. While the status's
    * `enterpriseOffer` is null our team is still agreeing its terms; once it is
    * set, subscribing to it starts the plan (enterprise.js). A standard plan
    * can be picked instead.
    */
   ENTERPRISE_OFFER_PENDING: "ENTERPRISE_OFFER_PENDING"
});

export const AccessLevel = Object.freeze({
   FULL: "full",
   BILLING_ONLY: "billing_only"
});

/** Stripe's own subscription statuses, which the API now passes through raw. */
export const SubscriptionStatus = Object.freeze({
   NONE: "none",
   TRIALING: "trialing",
   ACTIVE: "active",
   PAST_DUE: "past_due",
   PAUSED: "paused",
   UNPAID: "unpaid",
   CANCELED: "canceled",
   INCOMPLETE: "incomplete",
   INCOMPLETE_EXPIRED: "incomplete_expired"
});

/**
 * What came of the payment a request just made — `paymentOutcome` on the status
 * that setup-intents/{id}/complete, subscriptions and pay-outstanding answer
 * with. Null there when nothing was owed, and always null on billing-status.
 */
export const PaymentOutcome = Object.freeze({
   SUCCEEDED: "succeeded",
   /** Also answered as 409 PAYMENT_FAILED, with the status in `data`. */
   DECLINED: "declined",
   REQUIRES_ACTION: "requires_action"
});

/** What a status's `amountDue` is made of — its `kind`. */
export const AmountDueKind = Object.freeze({
   /**
    * One month of the plan, starting the day it is paid. The months missed
    * are written off, not charged (a monthly plan once access has stopped).
    */
   RESTART_PERIOD: "restart_period",
   /**
    * Every unpaid invoice, with the billing dates as they are (an annual plan,
    * a payment still in its grace period, a first invoice).
    */
   OVERDUE_INVOICES: "overdue_invoices",
   /**
    * The invoices an annual plan left open when it ended unpaid. Paying them
    * starts no period and no subscription.
    */
   OUTSTANDING_DEBT: "outstanding_debt"
});

/** Where the app sends people for what only a person can sort out. */
export const SUPPORT_EMAIL = "info@kaptechgroup.com";

const GENERIC_ERROR = "We couldn't complete that. Please try again.";

// What each code tells the person looking at the screen. Worded for what they
// can do next, because a code only ever reaches the screen when something has
// to be done differently.
const CODE_MESSAGES = {
   [BillingCode.UNAUTHENTICATED]:
      "Your session has expired. Please try again, or sign in again if this keeps happening.",
   [BillingCode.NOT_ORGANIZATION_ADMIN]:
      "Only an admin of this organization can manage its billing.",
   [BillingCode.ORGANIZATION_NOT_FOUND]:
      "We couldn't find this organization. Please sign in again.",
   [BillingCode.PACKET_NOT_FOUND]: "That plan is no longer available. Please pick another one.",
   [BillingCode.PACKET_HAS_NO_PRICE]:
      "That plan can't be bought right now. Please pick another plan, or contact us.",
   [BillingCode.NO_SUBSCRIPTION]:
      "This organization doesn't have a subscription yet. Pick a plan to start one.",
   [BillingCode.COMMITMENT_ACTIVE]:
      "Your annual plan is a 12-month commitment. You can move to a monthly plan only in its last month. A bigger annual plan applies now, and a smaller plan starts when the commitment ends.",
   [BillingCode.NO_STRIPE_CUSTOMER]: "Please add your billing details first.",
   [BillingCode.NOT_STRIPE]:
      "This organization isn't billed by card. Please contact us to change how it is billed.",
   [BillingCode.EMAIL_NOT_CONFIRMED]: "Please confirm your email address first.",
   [BillingCode.BILLING_PROFILE_INCOMPLETE]: "Please complete your billing details first.",
   [BillingCode.BILLING_PROFILE_INVALID]: "Some of your billing details need another look.",
   [BillingCode.TAX_ID_INVALID]: "That tax number doesn't look valid for the selected country.",
   [BillingCode.CONSENT_REQUIRED]:
      "Please confirm that this card may be charged for your subscription.",
   [BillingCode.CONSENT_AMOUNT_MISMATCH]:
      "The price has changed since it was shown. Please review it and confirm again.",
   [BillingCode.SETUP_INTENT_NOT_SUCCEEDED]:
      "Your bank didn't confirm this card. Please try again, or use a different card.",
   [BillingCode.SETUP_INTENT_FOREIGN]:
      "This card was set up for a different organization. Please start again.",
   [BillingCode.PAYMENT_ACTION_REQUIRED]: "Your bank needs you to confirm this payment.",
   [BillingCode.PAYMENT_FAILED]: "The payment was declined. Please try a different card.",
   [BillingCode.PAYMENT_METHOD_REQUIRED]: "There's no card on file. Please add a card to pay.",
   [BillingCode.PROMO_CODE_INVALID]: "That promo code isn't valid.",
   [BillingCode.PROMO_CODE_NOT_APPLICABLE]: "That promo code can't be used with this plan.",
   [BillingCode.BILLING_BUSY]:
      "Another billing change is still going through. Please try again in a moment.",
   [BillingCode.STRIPE_ERROR]:
      "Our payment provider couldn't complete this. Please try again in a moment.",
   [BillingCode.PLAN_LIMIT_EXCEEDED]:
      "Your organization has more active workspaces or projects than this plan includes. Archive the ones you don't need first.",
   [BillingCode.DOWNGRADE_REQUIRES_SUPPORT]: `Your subscription has been cancelled, so it can't be moved to a smaller plan here. Please contact support at ${SUPPORT_EMAIL} to change to a smaller plan.`,
   [BillingCode.OUTSTANDING_DEBT]:
      "Your previous annual plan still has an unpaid balance. Pay it first, then pick a plan.",
   [BillingCode.PLAN_CHANGE_NOT_SCHEDULED]:
      "The plan change couldn't be scheduled. Nothing has changed; please try again.",
   [BillingCode.PAYMENT_DUE]:
      "Your last payment hasn't gone through yet, so your plan can't be changed. Settle the payment first, then choose the plan again.",
   [BillingCode.NETWORK_ERROR]: "We couldn't reach KAPsurvey. Check your connection and try again.",
   [BillingCode.RATE_LIMITED]: "Too many attempts. Please wait a minute and try again."
};

// The codes whose server text says more than this app can: the reason a bank
// gave for declining, what exactly is wrong with a tax number or an address,
// the date a commitment ends. For these the server's wording is shown when it
// sent one. Shown, not read — nothing branches on it.
const SERVER_WORDED_CODES = new Set([
   BillingCode.COMMITMENT_ACTIVE,
   BillingCode.PAYMENT_FAILED,
   BillingCode.PLAN_LIMIT_EXCEEDED,
   BillingCode.PLAN_CHANGE_NOT_SCHEDULED,
   BillingCode.STRIPE_ERROR,
   BillingCode.TAX_ID_INVALID,
   BillingCode.BILLING_PROFILE_INVALID
]);

/** Every code the app has words for, so a test can hold the list to the API's. */
export const DESCRIBED_CODES = Object.freeze(Object.keys(CODE_MESSAGES));

/** What to tell somebody about a billing failure (a `BillingError`, or anything thrown). */
export const describeBillingError = (error) => {
   const code = error?.code;
   const serverMessage = typeof error?.serverMessage === "string" ? error.serverMessage : null;

   if (code && SERVER_WORDED_CODES.has(code) && serverMessage) return serverMessage;
   if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];

   return serverMessage || GENERIC_ERROR;
};

// What each reason for being held at billing_only is called, and what it asks
// of the person — shown at the top of the billing page and in the notice a
// 402 raises.
const REASON_COPY = {
   [BillingReason.NO_SUBSCRIPTION]: {
      title: "Choose a plan to get started",
      description:
         "Your account is set up but doesn't have a plan yet. Pick one below to start using KAPsurvey."
   },
   [BillingReason.TRIAL_ENDED_NO_PAYMENT_METHOD]: {
      title: "Your free trial has ended",
      description:
         "Add your billing details and a card to carry on. Your subscription restarts as soon as the card is saved."
   },
   [BillingReason.PAYMENT_FAILED]: {
      title: "Your last payment didn't go through",
      description:
         "The payment for your subscription was declined. Add a different card to settle it and restore access."
   },
   [BillingReason.PAYMENT_ACTION_REQUIRED]: {
      title: "Your bank needs you to confirm a payment",
      description:
         "The payment for your subscription is waiting for you to approve it with your bank (3-D Secure)."
   },
   [BillingReason.SUBSCRIPTION_CANCELED]: {
      title: "Your subscription has ended",
      // Whether the new one starts on a trial is the plans' to say, by the
      // status's `trialAvailable` (billingActions.js, offersTrial).
      description: "Choose a plan to start a new subscription."
   },
   [BillingReason.OUTSTANDING_DEBT]: {
      title: "Settle your balance",
      description:
         "Your annual plan ended with payments still due. Pay the outstanding balance, then pick a plan to start a new subscription."
   },
   [BillingReason.PAYMENT_METHOD_REQUIRED]: {
      title: "Add a card to start your plan",
      description:
         "Your plan is picked. Add your billing details and a card, and the first payment is taken as soon as the card is saved."
   },
   // Picking a standard plan instead drops the Enterprise request.
   [BillingReason.ENTERPRISE_OFFER_PENDING]: {
      title: "We are preparing your Enterprise plan",
      description:
         "Our team will contact you to agree its terms. If you'd rather start now, you can pick one of our standard plans below instead."
   }
};

// ENTERPRISE_OFFER_PENDING once our team has offered the terms.
const ENTERPRISE_OFFER_READY_COPY = {
   title: "Your Enterprise plan is ready",
   description:
      "Choose monthly or annual billing and subscribe. If there's no card on file, you add one next, and the plan starts with the first payment."
};

const UNKNOWN_REASON_COPY = {
   title: "Your subscription needs attention",
   description: "Something about your subscription has to be sorted out before you can carry on."
};

/** Every reason the app has words for, held to the API's list the same way. */
export const DESCRIBED_REASONS = Object.freeze(Object.keys(REASON_COPY));

/**
 * The title and description one reason is presented with. `status`, when there
 * is one, tells an Enterprise plan still being agreed from one whose terms are
 * offered.
 */
export const describeBillingReason = (reason, status) =>
   reason === BillingReason.ENTERPRISE_OFFER_PENDING && status?.enterpriseOffer
      ? ENTERPRISE_OFFER_READY_COPY
      : REASON_COPY[reason] ?? UNKNOWN_REASON_COPY;

// What somebody who can't manage an organization's billing is told when it is
// held: the billing page is not theirs, so they are kept out of the
// organization altogether, and told who has to sort it out.
const noActiveSubscription = (name) => `${name || "Your organization"} doesn't have an active subscription`;

const LOCK_TITLES = {
   [BillingReason.NO_SUBSCRIPTION]: noActiveSubscription,
   [BillingReason.PAYMENT_METHOD_REQUIRED]: noActiveSubscription,
   [BillingReason.ENTERPRISE_OFFER_PENDING]: noActiveSubscription,
   [BillingReason.TRIAL_ENDED_NO_PAYMENT_METHOD]: (name) =>
      `The free trial for ${name || "your organization"} has ended`,
   [BillingReason.SUBSCRIPTION_CANCELED]: (name) =>
      `The subscription for ${name || "your organization"} has been canceled`,
   [BillingReason.OUTSTANDING_DEBT]: (name) =>
      `The subscription for ${name || "your organization"} has been canceled`
};

const expiredLockTitle = (name) => `The subscription for ${name || "your organization"} has expired`;

/**
 * The title and description an organization held at billing_only is presented
 * with to a member who may not manage its billing — on the login form, on its
 * card in the organization list and in the notice that takes them out of it.
 */
export const describeOrganizationLock = (reason, organizationName) => ({
   title: (LOCK_TITLES[reason] ?? expiredLockTitle)(organizationName),
   description: "You'll have access again once its owner renews the subscription."
});
